import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import { DollarSign, Plus, Calendar, Users, Edit, Trash2, Search, Settings } from "lucide-react";
import { CurrencyDisplay } from "./CurrencySettings";
import { formatDateDDMonYYYY } from "../utils/dateFormat";

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const ROYALTY_TYPES = [
  "Sales Royalty",
  "Delivery Royalty",
  "Franchise Royalty",
  "Streaming Royalty",
  "Marketing Royalty",
  "Technology Royalty"
];

const RoyaltyEntry = ({ restaurants }) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  
  const [formData, setFormData] = useState({
    month: currentMonth,
    year: currentYear,
    payee_id: "",
    royalty_type: "",
    amount: "",
    restaurant_id: "",
    notes: "",
  });
  
  const [payees, setPayees] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Manage Payees Dialog State
  const [managePayeesDialog, setManagePayeesDialog] = useState(false);
  const [payeeSearchTerm, setPayeeSearchTerm] = useState("");
  const [addPayeeMode, setAddPayeeMode] = useState(false);
  const [newPayeeName, setNewPayeeName] = useState("");
  const [editPayeeDialog, setEditPayeeDialog] = useState({ isOpen: false, payee: null });
  const [editPayeeName, setEditPayeeName] = useState("");
  const [deletePayeeDialog, setDeletePayeeDialog] = useState({ isOpen: false, payeeId: null, payeeName: "" });

  useEffect(() => {
    fetchPayees();
  }, []);

  const fetchPayees = async () => {
    try {
      const response = await axiosInstance.get("/royalty-payees");
      setPayees(response.data);
    } catch (error) {
      console.error("Error fetching payees:", error);
    }
  };

  const handleAddPayee = async () => {
    if (!newPayeeName.trim()) {
      toast.error("Please enter payee name");
      return;
    }

    try {
      await axiosInstance.post("/royalty-payees", { name: newPayeeName.trim() });
      toast.success("Payee added successfully!");
      setNewPayeeName("");
      setAddPayeeMode(false);
      fetchPayees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add payee");
    }
  };

  const handleEditPayee = (payee) => {
    setEditPayeeName(payee.name);
    setEditPayeeDialog({ isOpen: true, payee });
  };

  const handleUpdatePayee = async () => {
    if (!editPayeeName.trim()) {
      toast.error("Please enter payee name");
      return;
    }

    try {
      await axiosInstance.put(`/royalty-payees/${editPayeeDialog.payee.id}`, { 
        name: editPayeeName.trim() 
      });
      toast.success("Payee updated successfully!");
      setEditPayeeDialog({ isOpen: false, payee: null });
      setEditPayeeName("");
      fetchPayees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update payee");
    }
  };

  const handleDeletePayee = async () => {
    try {
      await axiosInstance.delete(`/royalty-payees/${deletePayeeDialog.payeeId}`);
      toast.success("Payee deleted successfully!");
      setDeletePayeeDialog({ isOpen: false, payeeId: null, payeeName: "" });
      fetchPayees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete payee");
    }
  };

  const filteredPayees = payees.filter(payee =>
    payee.name.toLowerCase().includes(payeeSearchTerm.toLowerCase())
  );

  const validateForm = () => {
    if (!formData.month) {
      toast.error("Please select a month");
      return false;
    }
    if (!formData.year) {
      toast.error("Please select a year");
      return false;
    }
    if (!formData.payee_id) {
      toast.error("Please select a payee");
      return false;
    }
    if (!formData.royalty_type) {
      toast.error("Please select a royalty type");
      return false;
    }
    if (!formData.amount || isNaN(parseFloat(formData.amount)) || parseFloat(formData.amount) <= 0) {
      toast.error("Please enter a valid amount greater than 0");
      return false;
    }
    if (!formData.restaurant_id) {
      toast.error("Please select a restaurant");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        ...formData,
        month: parseInt(formData.month),
        year: parseInt(formData.year),
        amount: parseFloat(formData.amount),
      };

      await axiosInstance.post("/royalty-entries", payload);
      toast.success("Royalty Added Successfully!");
      
      // Reset form
      setFormData({
        month: currentMonth,
        year: currentYear,
        payee_id: "",
        royalty_type: "",
        amount: "",
        restaurant_id: "",
        notes: "",
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save royalty entry");
    } finally {
      setLoading(false);
    }
  };

  const generateYears = () => {
    const years = [];
    for (let i = currentYear - 5; i <= currentYear + 5; i++) {
      years.push(i);
    }
    return years;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Royalty Entry</h2>
          <p className="text-muted-foreground">Add new royalty payment entries</p>
        </div>
        <Button 
          onClick={() => setManagePayeesDialog(true)} 
          variant="outline" 
          className="gap-2"
          data-testid="manage-payees-btn"
        >
          <Settings className="w-4 h-4" />
          Manage Payees
        </Button>
      </div>

      {/* Entry Form */}
      <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-xl max-w-3xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
            New Royalty Entry
          </CardTitle>
          <CardDescription>
            Enter royalty payment details for the selected period
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Month and Year Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Month *</Label>
                <Select
                  value={formData.month.toString()}
                  onValueChange={(value) => setFormData({ ...formData, month: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((month) => (
                      <SelectItem key={month.value} value={month.value.toString()}>
                        {month.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Year *</Label>
                <Select
                  value={formData.year.toString()}
                  onValueChange={(value) => setFormData({ ...formData, year: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select year" />
                  </SelectTrigger>
                  <SelectContent>
                    {generateYears().map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Payee */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Royalty Payee *</Label>
                <Button 
                  type="button"
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    setManagePayeesDialog(true);
                    setAddPayeeMode(true);
                  }}
                  className="text-xs gap-1 h-auto py-1"
                >
                  <Plus className="w-3 h-3" />
                  Quick Add
                </Button>
              </div>
              <Select
                value={formData.payee_id}
                onValueChange={(value) => setFormData({ ...formData, payee_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select payee" />
                </SelectTrigger>
                <SelectContent>
                  {payees.map((payee) => (
                    <SelectItem key={payee.id} value={payee.id}>
                      {payee.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Royalty Type */}
            <div className="space-y-2">
              <Label>Royalty Type *</Label>
              <Select
                value={formData.royalty_type}
                onValueChange={(value) => setFormData({ ...formData, royalty_type: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select royalty type" />
                </SelectTrigger>
                <SelectContent>
                  {ROYALTY_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Restaurant */}
            <div className="space-y-2">
              <Label>Restaurant *</Label>
              <Select
                value={formData.restaurant_id || undefined}
                onValueChange={(value) => setFormData({ ...formData, restaurant_id: value })}
              >
                <SelectTrigger data-testid="royalty-restaurant-select">
                  <SelectValue placeholder="Select restaurant" />
                </SelectTrigger>
                <SelectContent>
                  {restaurants && restaurants.map((restaurant) => (
                    <SelectItem key={restaurant.id} value={restaurant.id}>
                      {restaurant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label>Royalty Amount (INR) *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 font-semibold">₹</span>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                placeholder="Add any additional notes or remarks..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
            >
              {loading ? "Saving..." : "Save Royalty"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Manage Payees Dialog */}
      <Dialog open={managePayeesDialog} onOpenChange={(open) => {
        setManagePayeesDialog(open);
        if (!open) {
          setAddPayeeMode(false);
          setPayeeSearchTerm("");
          setNewPayeeName("");
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Manage Payees
            </DialogTitle>
            <DialogDescription>
              Add, edit, or remove royalty payees
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col space-y-4">
            {/* Search and Add Row */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search payees..."
                  value={payeeSearchTerm}
                  onChange={(e) => setPayeeSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button 
                onClick={() => setAddPayeeMode(!addPayeeMode)}
                variant={addPayeeMode ? "secondary" : "default"}
                size="sm"
                className="gap-2"
              >
                <Plus className="w-4 h-4" />
                {addPayeeMode ? "Cancel" : "Add New"}
              </Button>
            </div>

            {/* Add New Payee Form */}
            {addPayeeMode && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-end gap-3">
                  <div className="flex-1 space-y-2">
                    <Label>New Payee Name</Label>
                    <Input
                      placeholder="e.g., Zomato, Swiggy, Magic Pin"
                      value={newPayeeName}
                      onChange={(e) => setNewPayeeName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddPayee()}
                    />
                  </div>
                  <Button onClick={handleAddPayee}>
                    Add Payee
                  </Button>
                </div>
              </div>
            )}

            {/* Payees List */}
            <div className="flex-1 overflow-y-auto border rounded-lg">
              {filteredPayees.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-center">
                  <Users className="w-12 h-12 text-gray-300 mb-3" />
                  <p className="text-gray-500">
                    {payeeSearchTerm ? "No payees match your search" : "No payees yet. Add your first payee above."}
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {filteredPayees.map((payee) => (
                    <div 
                      key={payee.id} 
                      className="flex items-center justify-between p-4 hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-semibold">
                          {payee.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{payee.name}</p>
                          <p className="text-xs text-gray-500">
                            Added {formatDateDDMonYYYY(payee.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditPayee(payee)}
                          className="gap-1"
                        >
                          <Edit className="w-4 h-4" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeletePayeeDialog({ 
                            isOpen: true, 
                            payeeId: payee.id,
                            payeeName: payee.name 
                          })}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <p className="text-xs text-gray-500 text-center">
              {payees.length} payee{payees.length !== 1 ? 's' : ''} total
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setManagePayeesDialog(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Payee Dialog */}
      <Dialog open={editPayeeDialog.isOpen} onOpenChange={(open) => !open && setEditPayeeDialog({ isOpen: false, payee: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Payee</DialogTitle>
            <DialogDescription>
              Update the payee name
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Payee Name *</Label>
              <Input
                placeholder="e.g., Zomato, Swiggy, Magic Pin"
                value={editPayeeName}
                onChange={(e) => setEditPayeeName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleUpdatePayee()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPayeeDialog({ isOpen: false, payee: null })}>
              Cancel
            </Button>
            <Button onClick={handleUpdatePayee}>
              Update Payee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deletePayeeDialog.isOpen} onOpenChange={(open) => !open && setDeletePayeeDialog({ isOpen: false, payeeId: null, payeeName: "" })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Payee</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>"{deletePayeeDialog.payeeName}"</strong>? 
              This action cannot be undone. Any existing royalty entries linked to this payee will need to be updated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePayee} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RoyaltyEntry;
